import { useAuth, useConfirmation, useToast } from '@/app/providers';
import { Badge, Button, DataTable, Select, type ColumnDef } from '@/core/ui/components';
import type { PaginationMeta } from '@yawi/domain';
import type { TenantAdmin, TenantStatus } from '@yawi/types';
import {
  Building2,
  ExternalLink,
  Eye,
  MoreVertical,
  Pause,
  Play,
  Plus,
  Trash2,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenants } from '../hooks/useTenants';

const STATUS_BADGES: Record<string, { variant: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  active: { variant: 'success', label: 'Active' },
  ACTIVE: { variant: 'success', label: 'Active' },
  suspended: { variant: 'warning', label: 'Suspended' },
  SUSPENDED: { variant: 'warning', label: 'Suspended' },
  pending: { variant: 'default', label: 'Pending' },
  PENDING: { variant: 'default', label: 'Pending' },
  cancelled: { variant: 'error', label: 'Cancelled' },
  CANCELLED: { variant: 'error', label: 'Cancelled' },
  PENDING_SETUP: { variant: 'default', label: 'Pending setup' },
  DELETED: { variant: 'error', label: 'Deleted' },
};

// Valores de TenantStatus del backend (tenant.entity.ts)
const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'SUSPENDED', label: 'Suspended' },
  { value: 'PENDING_SETUP', label: 'Pending setup' },
  { value: 'DELETED', label: 'Deleted' },
];

export function TenantListPage() {
  const navigate = useNavigate();
  const { accessTenant } = useAuth();
  const { confirm } = useConfirmation();
  const { showToast } = useToast();

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState<TenantStatus | ''>('');
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Debounce the search term so we don't hit the API on every keystroke. The
  // page resets in the same update, so a new search fetches once (page 1)
  // instead of fetching the old page first.
  useEffect(() => {
    const timer = setTimeout(() => {
      const next = search.trim();
      setDebouncedSearch((prev) => {
        if (prev !== next) setPage(1);
        return next;
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { tenants, meta, isLoading, suspendTenant, activateTenant, deleteTenant } = useTenants({
    page,
    limit: pageSize,
    search: debouncedSearch,
    status: statusFilter || undefined,
  });

  // Server-driven pagination meta (fallback while first load resolves)
  const paginationMeta: PaginationMeta = meta ?? {
    page,
    limit: pageSize,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  };

  const handleSearchChange = (value: string) => {
    setSearch(value);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(1);
  };

  const handleViewTenant = (tenant: TenantAdmin) => {
    navigate(`/tenants/${tenant.id}`);
  };

  const handleAccessTenant = async (tenant: TenantAdmin) => {
    try {
      await accessTenant(tenant.id);
      window.open(`https://${tenant.customDomain || `${tenant.slug}.hommi.mx`}`, '_blank');
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to access tenant');
    }
  };

  const handleSuspendTenant = async (tenant: TenantAdmin) => {
    const confirmed = await confirm({
      title: 'Suspend Tenant',
      message: `Are you sure you want to suspend "${tenant.name}"? Users will not be able to access the platform.`,
      variant: 'warning',
      confirmText: 'Suspend',
    });

    if (confirmed) {
      await suspendTenant(tenant.id);
    }
  };

  const handleActivateTenant = async (tenant: TenantAdmin) => {
    await activateTenant(tenant.id);
  };

  const handleDeleteTenant = async (tenant: TenantAdmin) => {
    const confirmed = await confirm({
      title: 'Delete Tenant',
      message: `Are you sure you want to delete "${tenant.name}"? This action cannot be undone and all data will be permanently lost.`,
      variant: 'danger',
      confirmText: 'Delete',
    });

    if (confirmed) {
      await deleteTenant(tenant.id);
    }
  };

  const columns: ColumnDef<TenantAdmin>[] = [
    {
      id: 'name',
      header: 'Tenant',
      cell: (tenant) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-white">{tenant.name}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {tenant.customDomain || `${tenant.slug}.hommi.mx`}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'plan',
      header: 'Plan',
      // Plan de la suscripción; `plan` es la columna legacy que Billing no actualiza.
      cell: (tenant) => <Badge variant="purple">{tenant.subscriptionPlan?.name ?? tenant.plan}</Badge>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (tenant) => {
        const badge = STATUS_BADGES[tenant.status] || STATUS_BADGES.pending;
        return <Badge variant={badge.variant}>{badge.label}</Badge>;
      },
    },
    {
      id: 'users',
      header: 'Users',
      cell: (tenant) => (
        <span className="text-gray-600 dark:text-gray-400">{tenant.usersCount ?? '-'}</span>
      ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      cell: (tenant) => (
        <span className="text-gray-600 dark:text-gray-400">
          {new Date(tenant.createdAt).toLocaleDateString()}
        </span>
      ),
    },
  ];

  const renderRowActions = (tenant: TenantAdmin) => (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpenMenuId(openMenuId === tenant.id ? null : tenant.id);
        }}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
      >
        <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
      </button>

      {openMenuId === tenant.id && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
          <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 shadow-lg z-20 py-1">
            <button
              onClick={() => {
                handleViewTenant(tenant);
                setOpenMenuId(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Eye className="w-4 h-4" />
              View Details
            </button>
            <button
              onClick={() => {
                handleAccessTenant(tenant);
                setOpenMenuId(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <ExternalLink className="w-4 h-4" />
              Access as Admin
            </button>
            <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
            {tenant.status === 'active' || tenant.status === 'ACTIVE' ? (
              <button
                onClick={() => {
                  handleSuspendTenant(tenant);
                  setOpenMenuId(null);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-amber-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <Pause className="w-4 h-4" />
                Suspend
              </button>
            ) : tenant.status === 'DELETED' ? null : (
              <button
                onClick={() => {
                  handleActivateTenant(tenant);
                  setOpenMenuId(null);
                }}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-gray-100 dark:hover:bg-gray-800"
              >
                <Play className="w-4 h-4" />
                Activate
              </button>
            )}
            <button
              onClick={() => {
                handleDeleteTenant(tenant);
                setOpenMenuId(null);
              }}
              className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </>
      )}
    </div>
  );

  return (
    <div className="h-full">
      <DataTable
        title="Tenants"
        data={tenants}
        columns={columns}
        keyExtractor={(tenant) => tenant.id}
        loading={isLoading}
        emptyMessage="No tenants found"
        onRowClick={handleViewTenant}
        rowActions={renderRowActions}
        searchValue={search}
        onSearchChange={handleSearchChange}
        searchPlaceholder="Search tenants..."
        meta={paginationMeta}
        onPageChange={setPage}
        pageSize={pageSize}
        onPageSizeChange={handlePageSizeChange}
        actions={
          <div className="flex items-center gap-3">
            <div className="w-44">
              <Select
                aria-label="Filter by status"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as TenantStatus | '');
                  setPage(1);
                }}
                options={STATUS_FILTER_OPTIONS}
              />
            </div>
            <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/tenants/new')}>
              Add Tenant
            </Button>
          </div>
        }
        minHeight="min-h-[calc(100vh-180px)]"
      />
    </div>
  );
}
